import React, { useEffect, useRef } from "react"
import { Link } from "gatsby"
import { Menu } from "react-feather"
import { CMSSubNav } from "./CMSSubNav"
import { SubMenuItem } from "./SubMenuItem"
import { useScroll } from "../../utils/functions"
import { Instagram } from "react-feather"
import logo from "../../assets/icons/logo.svg"
import logoWhite from "../../assets/icons/logo-white.svg"
import youtube from "../../assets/icons/youtubeDark.svg"
import tatler from "../../assets/icons/tatler.svg"
import royalCollege from "../../assets/images/royal-college.svg"
import aesthetics from "../../assets/images/aesthetics.webp"
import linkedin from "../../assets/icons/linkedin.svg"
import greatPatientExperience from "../../assets/images/2025-Great-Patient-Experience-Doctify.png"
import styles from "./header.module.css"
import containerClasses from "../../styles/container.module.css"
import buttons from "../../styles/buttons.module.css"

type HeaderProps = {
  siteTitle: string
  navLinks: Array<{
    name: string
    nameShort: string
    page: string
    id: number
    subLinks?: Array<{ name: string; page: string; id: number }>
  }>
  productItems: object
  navToggle: () => Function
  openMenu: number
  menuToggle: () => Function
  headerBg?: string
  headerColor?: string
  page: string
  overlayNav?: boolean | "invert"
}

const Navigation = ({
  navLinks,
  openMenu,
  menuToggle,
  productItems,
  page: pageRendered,
}) => {
  return (
    <nav>
      <ol className={`${styles.headerNavigationList} flex justify-end`}>
        {navLinks.map(({ name, nameShort, page, subLinks, id, products }) => {
          if (products) {
            let data

            // @ts-ignore: graphql data
            if (products === "conditions") {
              data = productItems.conditions
            }

            // @ts-ignore: graphql data
            if (products === "oculoplasticServices") {
              data = productItems.oculoplasticServices
            }

            // @ts-ignore: graphql data
            if (products === "aestheticServices") {
              data = productItems.aestheticServices
            }

            data.edges.sort((a, b) =>
              a.node.childMarkdownRemark.frontmatter.id >
              b.node.childMarkdownRemark.frontmatter.id
                ? 1
                : -1
            )

            const productsDestructured = data.edges.map(
              obj =>
                // @ts-ignore: graphql data
                obj.node.childMarkdownRemark.frontmatter
            )

            return (
              <CMSSubNav
                page={pageRendered === products}
                key={`navigation-item-${name}`}
                name={name}
                nameShort={nameShort}
                title="Treatments"
                items={productsDestructured}
                id={id}
                open={openMenu === id}
                openTrigger={menuToggle}
              />
            )
          }

          if (subLinks && subLinks.length > 0) {
            return (
              <SubMenuItem
                key={`navigation-item-${id}`}
                name={name}
                subLinks={subLinks}
                id={id}
                open={openMenu === id}
                openTrigger={menuToggle}
              />
            )
          }
          return (
            <li
              className={`${styles.headerNavigationLinkItem} ${
                name === pageRendered ? `afterBorder` : ``
              }`}
              data-testid="headerNavigationLink"
              key={`navigation-item-${id}`}
            >
              <Link className={styles.headerNavigationLink} to={`${page}/`}>
                {nameShort ? (
                  <>
                    <span className="font-medium xl:hidden">{nameShort}</span>
                    {/* This styles the header link In The Press */}
                    <span className="hidden font-medium uppercase xl:block font-13">
                      {name}
                    </span>
                  </>
                ) : (
                  <span>{name}</span>
                )}
              </Link>
            </li>
          )
        })}
        <li
          className={`${styles.headerNavigationLinkItem} ${
            pageRendered === "Contact" ? `afterBorder` : ``
          }`}
        >
          <Link className={styles.headerNavigationLink} to="/contact/">
            Contact
          </Link>
        </li>
      </ol>
    </nav>
  )
}
export const Header = ({
  siteTitle,
  navLinks,
  navToggle,
  openMenu,
  menuToggle,
  headerBg,
  headerColor,
  productItems,
  page,
  overlayNav,
}: HeaderProps) => {
  const { scrollY, scrollDirection } = useScroll()
  const headerRef = useRef<HTMLDivElement | null>(null)

  // Publish the live header height as a CSS variable so the home hero
  // (and any other consumer) can offset content by exactly the nav height,
  // automatically updating on resize or when nav padding/content changes.
  useEffect(() => {
    if (typeof window === "undefined" || !headerRef.current) return

    const update = () => {
      const h = headerRef.current?.offsetHeight ?? 0
      document.documentElement.style.setProperty("--header-height", `${h}px`)
    }

    update()

    const RO = (window as any).ResizeObserver
    const ro = RO ? new RO(update) : null
    if (ro && headerRef.current) ro.observe(headerRef.current)
    window.addEventListener("resize", update)

    return () => {
      ro?.disconnect()
      window.removeEventListener("resize", update)
    }
  }, [])

  const transparentOverlay = (page === "Home" || overlayNav) && scrollY <= 400
  // overlayNav === "invert" additionally switches the transparent nav to
  // white text and the white logo, for pages whose hero is dark at the top.
  const inverted = overlayNav === "invert" && transparentOverlay

  return (
    <div
      ref={headerRef}
      className={`
      ${headerBg ? headerBg : "bg-white shadow lg:pb-0"}
      ${headerColor ? headerColor : ""}
      ${scrollY > 400 ? styles.headerContainerFixed : ""}
      ${
        scrollY > 400 && scrollDirection === "down"
          ? styles.headerContainerRetreat
          : ""
      }
      ${transparentOverlay ? styles.headerContainerTransparent : ""}
      ${inverted ? styles.headerInverted : ""}
       ${styles.headerContainer}`}
    >
      {/* USPs (moved below hero on homepage) */}
      <div className="hidden col-span-4 md:col-span-8 lg:col-span-12">
        <div
          className="hidden"
          style={{ height: "2px", backgroundColor: "#0A8B9B" }}
        />
        <div className={`bg-white py-4 hidden border-b border-gray-100`}>
          <div
            className={`
      ${containerClasses.container}
      `}
          >
            <div className="flex justify-between md:col-span-8 lg:col-span-12">
              <div className="flex flex-row items-center">
                {" "}
                <img
                  loading="lazy"
                  alt=""
                  className="h-4 mr-3 lg:mt-0"
                  src={aesthetics}
                />
                <p className="mb-0 font-mono text-xs leading-none text-blackNew uppercase lg:mt-0">
                  Journal Advisor
                </p>
              </div>
              <div className="flex flex-row items-center">
                <img
                  loading="lazy"
                  alt=""
                  className="h-4 mr-3 lg:mt-0"
                  src={tatler}
                />
                <p className="mb-0 font-mono text-xs leading-none text-blackNew uppercase lg:mt-0">
                  Recommended Surgeon
                </p>
              </div>
              <div className="flex flex-row items-center">
                <img
                  loading="lazy"
                  alt=""
                  className="h-4 mr-3 lg:mt-0"
                  src={royalCollege}
                />
                <p className="mb-0 font-mono text-xs leading-none text-blackNew uppercase lg:mt-0">
                  RCOphth Examiner
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        className={`text-blackNew flex items-center justify-center md:justify-between ${containerClasses.container}`}
      >
        <div className="flex items-center col-span-4 py-5 lg:py-3 xl:py-4 md:col-span-8 lg:col-span-12">
          <Link className="content-center flex-grow lg:flex-grow-0 lg:block" to="/">
            <img
              loading="lazy"
              width="256"
              height="32"
              className={`${styles.headerLogoImage} lg:h-64 pr-0!`}
              src={inverted ? logoWhite : logo}
              alt={siteTitle}
            />
          </Link>
          {/* Inline nav — desktop only */}
          <div className="hidden lg:block flex-1 pl-8">
            <Navigation
              productItems={productItems}
              navLinks={navLinks}
              openMenu={openMenu}
              menuToggle={menuToggle}
              page={page}
            />
          </div>
          <div className="flex items-center">
            <Menu onClick={navToggle} className={styles.headerMenuIcon} />
          </div>
        </div>
      </div>
    </div>
  )
}
